import copy from 'clipboard-copy';
import { ClipboardEvent, useCallback } from 'react';

const COPY_CONTAINER_ATTRIBUTE = 'data-copy-container';
const NO_COPY_ATTRIBUTE = 'data-no-copy';
const PLAINTEXT_PREFIX_ATTRIBUTE = 'data-plaintext-prefix';

interface CustomCopyBehaviorOptions {
  /** Ensures this element always wraps its children when they are copied  */
  isCopyContainer?: boolean;
  /** Prevents this element and its children from being copied */
  excludeFromCopy?: boolean;
  /** Adds this string as a prefix to this element's children when it is used as plaintext */
  plaintextPrefix?: string;
}

const BLOCK_TAGS = new Set([
  'P',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'UL',
  'OL',
  'PRE',
  'BLOCKQUOTE',
  'TABLE',
  'HR',
]);

const isBlock = (node: Node) =>
  node.nodeType === Node.ELEMENT_NODE &&
  BLOCK_TAGS.has((node as Element).tagName);

// Inline content inside a block: formatting tags drop away and their text stays on
// the same line. Only <br> breaks a line.
const inlineText = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return (node.textContent ?? '').replace(/\s+/g, ' ');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as Element;
  if (el.tagName === 'BR') return '\n';
  if (el.tagName === 'IMG') return el.getAttribute('alt') ?? '';
  const text = Array.from(el.childNodes).map(inlineText).join('');
  const href = el.tagName === 'A' ? el.getAttribute('href') : null;
  // Keep the destination, which plain text would otherwise lose.
  return href && href !== text.trim() ? `${text} (${href})` : text;
};

const listText = (list: Element): string => {
  const start = Number(list.getAttribute('start') ?? 1);
  return Array.from(list.children)
    .filter((child) => child.tagName === 'LI')
    .map((item, index) => {
      const marker = list.tagName === 'OL' ? `${start + index}. ` : '• ';
      // A nested list stays tight under its item rather than a paragraph apart.
      const [first = '', ...rest] = blocksText(item, '\n').split('\n');
      const hanging = ' '.repeat(marker.length);
      return [marker + first, ...rest.map((l) => (l ? hanging + l : l))].join(
        '\n',
      );
    })
    .join('\n');
};

const blockText = (el: Element): string => {
  switch (el.tagName) {
    case 'UL':
    case 'OL':
      return listText(el);
    case 'PRE':
      return (el.textContent ?? '').replace(/\n$/, '');
    case 'TABLE':
      return Array.from(el.querySelectorAll('tr'))
        .map((row) =>
          Array.from(row.children)
            .map((cell) => inlineText(cell).trim())
            .join('\t'),
        )
        .join('\n');
    case 'HR':
      return '';
    default:
      return blocksText(el);
  }
};

// Blocks are separated by a blank line; runs of inline content between them
// become their own block.
const blocksText = (el: Element, separator = '\n\n'): string => {
  const blocks: string[] = [];
  let inline = '';
  const flushInline = () => {
    const text = inline
      .split('\n')
      .map((line) => line.trim())
      .join('\n')
      .trim();
    if (text) blocks.push(text);
    inline = '';
  };
  for (const child of Array.from(el.childNodes)) {
    if (isBlock(child)) {
      flushInline();
      const text = blockText(child as Element);
      if (text) blocks.push(text);
    } else {
      inline += inlineText(child);
    }
  }
  flushInline();
  return blocks.join(separator);
};

/** Readable plain text for rendered HTML, without any markup or markdown syntax. */
export const htmlToPlainText = (html: string): string =>
  blocksText(new DOMParser().parseFromString(html, 'text/html').body);

/**
 * Copies formatted HTML alongside a plain-text version, so rich editors keep the
 * formatting and plain fields get the text. Browsers without ClipboardItem, or
 * that reject the rich write, still get the plain text.
 */
export const copyRichText = async ({
  html,
  plainText,
}: {
  html: string;
  plainText: string;
}) => {
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
        }),
      ]);
      return;
    } catch {
      // Fall through to the plain-text copy below.
    }
  }
  await copy(plainText);
};

export const addCustomCopyBehavior = ({
  isCopyContainer,
  excludeFromCopy,
  plaintextPrefix,
}: CustomCopyBehaviorOptions) => {
  const dataAttributes: Record<string, string | boolean> = {};

  if (isCopyContainer) {
    dataAttributes[COPY_CONTAINER_ATTRIBUTE] = true;
  }
  if (excludeFromCopy) {
    dataAttributes[NO_COPY_ATTRIBUTE] = true;
  }
  if (plaintextPrefix) {
    dataAttributes[PLAINTEXT_PREFIX_ATTRIBUTE] = plaintextPrefix;
  }

  return dataAttributes;
};

// gets rid of additional newlines that come from block elements
// and handles indentation for list items
const extractTextContent = (element: Node, indentLevel: number = 0): string => {
  const children = Array.from(element.childNodes);
  const parts: string[] = [];
  let currentTextGroup = '';

  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent || '';
      // Accumulate consecutive text nodes together
      if (text.trim()) {
        currentTextGroup += text;
      }
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      // Flush any accumulated text from consecutive text nodes
      if (currentTextGroup) {
        parts.push(currentTextGroup);
        currentTextGroup = '';
      }

      const elementChild = child as Element;
      if (elementChild.tagName === 'BR') {
        parts.push('');
      } else if (elementChild.tagName === 'LI') {
        parts.push(
          '   '.repeat(indentLevel) +
            extractTextContent(child, indentLevel + 1),
        );
      } else {
        parts.push(extractTextContent(child, indentLevel));
      }
    }
  }

  // Flush any remaining text
  if (currentTextGroup) {
    parts.push(currentTextGroup);
  }

  return parts.join('\n');
};

interface CopyHandlerOptions {
  skipTargetElements?: Array<'textarea' | 'input'>;
}

export const useCopyHandler = (options: CopyHandlerOptions = {}) => {
  const { skipTargetElements = [] } = options;

  const handleCopy = useCallback(
    (event: ClipboardEvent) => {
      for (const skipTargetElement of skipTargetElements) {
        switch (skipTargetElement) {
          case 'textarea':
            if (event.target instanceof HTMLTextAreaElement) return;
            break;
          case 'input':
            if (event.target instanceof HTMLInputElement) return;
            break;
        }
      }

      event.preventDefault();

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const fragment = range.cloneContents();
      const div = document.createElement('div');

      // determine what the outermost container for the copy selection should be
      const commonAncestor = range.commonAncestorContainer;
      const ancestorElement =
        commonAncestor.nodeType === Node.ELEMENT_NODE
          ? (commonAncestor as Element)
          : commonAncestor.parentElement;
      if (
        ancestorElement &&
        COPY_CONTAINER_ATTRIBUTE in ancestorElement.attributes
      ) {
        const listClone = document.createElement(
          ancestorElement.tagName.toLowerCase(),
        );
        for (const attr of Array.from(ancestorElement.attributes)) {
          if (attr.name === PLAINTEXT_PREFIX_ATTRIBUTE) continue;
          listClone.setAttribute(attr.name, attr.value);
        }
        listClone.appendChild(fragment);
        div.appendChild(listClone);
      } else {
        div.appendChild(fragment);
      }

      // remove elements that shouldn't be copied
      const noCopyElements = div.querySelectorAll(`[${NO_COPY_ATTRIBUTE}]`);
      noCopyElements.forEach((el) => el.remove());

      event.clipboardData.setData('text/html', div.innerHTML);

      // add plaintext-only prefixes to copy data
      const hasPlaintextPrefix = div.querySelectorAll(
        `[${PLAINTEXT_PREFIX_ATTRIBUTE}]`,
      );
      hasPlaintextPrefix.forEach((el) => {
        const prefixContent = el.getAttribute(PLAINTEXT_PREFIX_ATTRIBUTE);
        if (prefixContent) {
          const textNode = document.createTextNode(prefixContent);
          el.insertBefore(textNode, el.firstChild);
        }
      });

      event.clipboardData.setData('text/plain', extractTextContent(div));
    },
    [skipTargetElements],
  );

  return handleCopy;
};
